import { Box, Group, Popover, Select, Text, useMantineTheme } from '@mantine/core';
import { IconUser } from '@tabler/icons-react';
import { CcAssigneesBadge } from 'components/CcAssigneesBadge';
import { TaskBadge } from 'components/TaskBadge';
import { useProfile } from 'init/use-enforce-profile';
import { useUpdateTask } from 'modules/Tasks/api/use-update-task';
import { useState } from 'react';
import { useLoadLightUsers } from 'shared/hooks/use-load-light-users';
import type { Task } from 'shared/hooks/use-load-tasks';

type EditableAssigneeBadgeProps = {
  task: Task;
};

export const EditableAssigneeBadge = ({ task }: EditableAssigneeBadgeProps) => {
  const theme = useMantineTheme();

  const profile = useProfile();
  const canReassign = profile.isAdmin || profile.roles.includes('all_invoices');

  const { users = [] } = useLoadLightUsers(false, { query: '' }, { isEnabled: canReassign });
  const { updateTask, isUpdateTaskLoading } = useUpdateTask();

  const [isPopoverOpened, setIsPopoverOpened] = useState(false);

  const handleChange = (value: string | null) => {
    if (!value) return;

    updateTask(
      { id: task.id, data: { userId: value } },
      { onSettled: () => setIsPopoverOpened(false) },
    );
  };

  const assigneeIcon = <IconUser size={18} stroke={1.5} color={theme.colors.purple[5]} />;

  if (!canReassign)
    return (
      <Group gap={6} wrap="nowrap" onClick={e => e.stopPropagation()}>
        <TaskBadge icon={assigneeIcon}>
          <Text size="sm">{`${task.assignee.first_name} ${task.assignee.last_name}`}</Text>
        </TaskBadge>
        <CcAssigneesBadge assignees={task.additional_assignees} />
      </Group>
    );

  return (
    <Group gap={6} wrap="nowrap" onClick={e => e.stopPropagation()}>
      <Popover
        opened={isPopoverOpened}
        onChange={setIsPopoverOpened}
        position="bottom-start"
        withinPortal
      >
        <Popover.Target>
          <Box>
            <TaskBadge icon={assigneeIcon} onClick={() => setIsPopoverOpened(o => !o)}>
              <Text size="sm">{`${task.assignee.first_name} ${task.assignee.last_name}`}</Text>
            </TaskBadge>
          </Box>
        </Popover.Target>
        <Popover.Dropdown p="xs">
          <Select
            data={users.map(u => ({
              label: u.name.full,
              value: String(u.id),
            }))}
            value={String(task.assignee.id)}
            onChange={handleChange}
            disabled={isUpdateTaskLoading}
            searchable
            size="sm"
            w={200}
          />
        </Popover.Dropdown>
      </Popover>
      <CcAssigneesBadge assignees={task.additional_assignees} />
    </Group>
  );
};
